import { useEffect, useState } from "react";
import { Building2, Package, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/lib/api";
import { Kpi, PageHead } from "@/components/Bits";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function InvestorDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/reports/investor")
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || "Unable to load your studio"));
  }, []);

  if (err)
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700" data-testid="investor-error">
        {err}
      </div>
    );
  if (!data) return <div className="p-8 text-sm text-slate-500">Loading your studio…</div>;

  const { studio, revenue, jobs, orders, trend } = data;

  return (
    <div data-testid="investor-dashboard">
      <PageHead
        title={studio.name}
        subtitle={`${[studio.city, studio.region].filter(Boolean).join(" · ")} · investor view (read-only)`}
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Revenue (this month)" value={money(revenue.month)} testid="investor-kpi-revenue-month" />
        <Kpi label="Revenue (all time)" value={money(revenue.total)} accent="text-emerald-600" testid="investor-kpi-revenue-total" />
        <Kpi label="Jobs delivered (mo)" value={jobs.delivered_this_month} testid="investor-kpi-jobs-month" />
        <Kpi label="Jobs in progress" value={jobs.in_progress} testid="investor-kpi-inprogress" />
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <Card className="border-brand-line shadow-none">
          <CardContent className="flex items-center gap-3 p-5">
            <Wrench className="h-5 w-5 text-slate-400" />
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Total jobs</p>
              <p className="font-display text-2xl font-semibold text-brand-ink" data-testid="investor-total-jobs">
                {jobs.total}
              </p>
              <p className="text-xs text-slate-500">{jobs.delivered} delivered all-time</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-brand-line shadow-none">
          <CardContent className="flex items-center gap-3 p-5">
            <Package className="h-5 w-5 text-slate-400" />
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Material orders</p>
              <p className="font-display text-2xl font-semibold text-brand-ink" data-testid="investor-orders">
                {orders.total}
              </p>
              <p className="text-xs text-slate-500">{orders.delivered} delivered</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-brand-line shadow-none">
          <CardContent className="flex items-center gap-3 p-5">
            <Building2 className="h-5 w-5 text-slate-400" />
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Subscription</p>
              <p className="font-display text-lg font-semibold capitalize text-brand-ink" data-testid="investor-subscription">
                {studio.subscription_status || "—"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-brand-line shadow-none" data-testid="investor-trend">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Recent activity — delivered jobs & revenue</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Delivered</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trend.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="py-6 text-center text-sm text-slate-400">
                    No activity yet.
                  </TableCell>
                </TableRow>
              )}
              {trend.map((t) => (
                <TableRow key={t.month} className="hover:bg-brand-tint/60">
                  <TableCell className="font-medium">{t.month}</TableCell>
                  <TableCell className="text-right tabular-nums">{t.delivered}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(t.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
